import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';

import { getEventSectionsLeaderboard } from '../../features/leaderboard/leaderboardSlice';
import { filterSectionsByVenue } from '../../helpers/tvLogs';
import BSCircularProgress from '../ui/progress/circularProgress';
import SectionTotalsCard from '../ui/scoreboard/sectionTotalsCard';
import { useParams, useSearchParams } from 'react-router-dom';

import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { useColorMode } from '../../hooks/colorModeProvider';
import { setEvent, setLoadingEvent } from '../../features/event/eventSlice';

const MAX_RETRIES = 3;
const RETRY_DELAY = 5000; // 5 seconds
const SECTIONS_PER_PAGE = 8;
const AUTO_PAGE_INTERVAL_MS = 20000;

const ForTVLogs = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { eventId } = useParams();

  const event = useSelector((state) => state.event.event);
  const { mode, setMode } = useColorMode();
  const initialModeRef = useRef(mode);

  const loadingLeaderboard = useSelector((state) => state.leaderboard.loading);
  const sectionsLeaderboard = useSelector((state) => state.leaderboard.sectionLeaderboard);
  const venueFilter = String(searchParams.get('venue') || '').trim();
  const visibleSections = useMemo(
    () => filterSectionsByVenue(sectionsLeaderboard, venueFilter),
    [sectionsLeaderboard, venueFilter]
  );
  const initialPage = Number(searchParams.get('page')) || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const totalPages = Math.max(1, Math.ceil(visibleSections.length / SECTIONS_PER_PAGE));

  const startIdx = (currentPage - 1) * SECTIONS_PER_PAGE;
  const endIdx = startIdx + SECTIONS_PER_PAGE;
  const pagedSections = visibleSections.slice(startIdx, endIdx);

  const retryCount = useRef(0);

  useEffect(() => {
    if (!eventId) return;

    let isMounted = true;

    const loadEvent = () => {
      dispatch(setLoadingEvent(true));
      const docRef = doc(store, 'events', eventId);

      onSnapshot(
        docRef,
        (docSnap) => {
          if (!isMounted) return;
          if (docSnap.exists()) {
            const eventData = { id: docSnap.id, ...docSnap.data() };
            dispatch(setEvent(eventData));
            retryCount.current = 0; // reset on success
          } else {
            console.log('No such document!');
            if (retryCount.current < MAX_RETRIES) {
              retryCount.current += 1;
              setTimeout(loadEvent, RETRY_DELAY);
            }
          }
          dispatch(setLoadingEvent(false));
        },
        (error) => {
          if (!isMounted) return;
          console.error('Error loading event:', error);
          if (retryCount.current < MAX_RETRIES) {
            retryCount.current += 1;
            setTimeout(loadEvent, RETRY_DELAY);
          }
          dispatch(setLoadingEvent(false));
        }
      );
    };

    loadEvent();

    return () => {
      isMounted = false;
    };
  }, [eventId, dispatch]);

  // Follow the device/OS colour scheme instead of forcing light mode, so the
  // TV log renders dark wherever the OS/browser is set to dark - and keeps
  // following it if the setting changes while the view is open.
  useEffect(() => {
    const previousMode = initialModeRef.current;
    const darkQuery =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const handleChange = (event) => {
      setMode(event.matches ? 'dark' : 'light');
    };

    if (darkQuery) {
      setMode(darkQuery.matches ? 'dark' : 'light');
      darkQuery.addEventListener('change', handleChange);
    }

    return () => {
      if (darkQuery) {
        darkQuery.removeEventListener('change', handleChange);
      }
      setMode(previousMode);
    };
  }, [setMode]);

  useEffect(() => {
    if (event) {
      dispatch(getEventSectionsLeaderboard({ eventId: event.id }));
    }
  }, [dispatch, event]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (loadingLeaderboard || totalPages <= 1) {
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentPage((prevPage) => (prevPage >= totalPages ? 1 : prevPage + 1));
    }, AUTO_PAGE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [loadingLeaderboard, totalPages]);

  return !event ? (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent="center"
      minHeight="100vh">
      <BSCircularProgress caption="Loading event..." size={40} />
    </Box>
  ) : (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      sx={{
        width: '100%',
        backgroundColor: 'transparent',
        px: 3,
        py: 2
      }}>
      {loadingLeaderboard ? (
        <BSCircularProgress caption={'Loading...'} size={30} />
      ) : pagedSections.length > 0 ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            justifyContent: 'center',
            alignItems: 'start'
          }}>
          {pagedSections.map((section, index) => (
            <Box
              key={section.id || index}
              sx={{
                flex: '1 1 48%',
                maxWidth: '49%',
                minWidth: 380,
                p: 0.5
              }}>
              <SectionTotalsCard section={section} isTv={true} showReserves />
            </Box>
          ))}
        </Box>
      ) : (
        <Box color="text.secondary">
          {venueFilter
            ? `No sections are available for ${venueFilter}.`
            : 'No sections available.'}
        </Box>
      )}
    </Box>
  );
};

export default ForTVLogs;
